import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  faEye,
  faPen,
  faSquarePlus,
  faTrashCan,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { IconProp } from '@fortawesome/fontawesome-svg-core';
import { ColumnDef } from "@tanstack/react-table";
import { Button, Container, Form, Spinner } from "react-bootstrap";
import CustomTable from "../../../Formularios/CustomTable/CustomTable";
import { useGetSucursalesByPlataforma } from "../../../../hooks/useGetSucursalesByPlataforma";
import { SucursalInterface } from "../../../../interfaces/SucursalInterface";
import ForbiddenMessage from "../../../Mensajes/ForbiddenMessage";

//context
import { AuthContext } from "../../../../context/AuthContext";
import { toast } from "react-toastify";
import useFetch from "../../../../hooks/useFetch";

//estilos
const classes = require("./SucursalesTable.module.css").default;

const SucursalesTable = () => {
  const authContext = useContext(AuthContext);
  const { sendRequest, error, setError } = useFetch();
  const [plataformaFiltro, setPlataformaFiltro] = useState<number | null>(null);
  const { data: sucursales, isLoading, refetch } = useGetSucursalesByPlataforma(plataformaFiltro);
  const navigate = useNavigate();

  //Función que elimina una sucursal.
  const deleteSucursal = useCallback(
    (idSucursal: number) => {
      if (
        window.confirm(
          "¿Estás seguro de que quieres eliminar esta sucursal?"
        )
      ) {
        sendRequest(
          {
            url: `/sucursal/${idSucursal}`,
            method: "DELETE",
          },
          () => {
            // Refrescar la lista después de eliminar
            refetch();
            
            toast.success("Sucursal eliminada correctamente", {
              position: "top-left",
              autoClose: 5000,
              hideProgressBar: false,
              closeOnClick: true,
              pauseOnHover: true,
              draggable: true,
              progress: undefined,
              theme: "light",
            });
          }
        );
      }
    },
    [sendRequest, refetch]
  );

  //Definición de las columnas de la tabla
  const columns = useMemo<ColumnDef<SucursalInterface>[]>(
    () => [
      {
        header: "ID",
        accessorKey: "id",
      },
      {
        header: "ID Sucursal",
        accessorKey: "id_nmp",
        cell: ({ row }) => (row.original as any).id_nmp || "-",
      },
      {
        header: "Nombre",
        accessorKey: "nombre",
      },
      {
        header: "Domicilio",
        accessorKey: "domicilio",
      },
      {
        header: "Estado",
        accessorFn: (row) => row.estado?.nombre ?? "-",
      },
      {
        header: "Latitud",
        accessorKey: "latitud",
      },
      {
        header: "Longitud",
        accessorKey: "longitud",
      },
      {
        header: "Acciones",
        cell: ({ row }) => (
          <div>
            <Button
              variant="info"
              size="sm"
              className="me-2"
              onClick={() =>
                navigate(`/main/catalogos/sucursales/details/${row.original.id}`)
              }
              title="Ver detalles"
            >
              <FontAwesomeIcon icon={faEye as IconProp} />
            </Button>
            {authContext.tokenCan("Sucursal:update") && (
              <Button
                variant="warning"
                size="sm"
                className="me-2"
                onClick={() =>
                  navigate(`/main/catalogos/sucursales/edit/${row.original.id}`)
                }
                title="Editar"
              >
                <FontAwesomeIcon icon={faPen as IconProp} />
              </Button>
            )}
            {authContext.tokenCan("Sucursal:destroy") && (authContext.usuario?.id === 137 || authContext.usuario?.id === 67) && (
              <Button
                variant="danger"
                size="sm"
                onClick={() => deleteSucursal(row.original.id)}
                title="Eliminar"
              >
                <FontAwesomeIcon icon={faTrashCan as IconProp} />
              </Button>
            )}
          </div>
        ),
      },
    ],
    [navigate, authContext, deleteSucursal]
  );

  //Mientras se carga el contenido se muestra un spinner
  if (isLoading || !sucursales) {
    return (
      <div className={classes.spinnerContainer}>
        <Spinner animation="grow" variant="info" />
      </div>
    );
  }

  //Si no tiene permisos para ver sucursales
  if (error === 403) {
    return <ForbiddenMessage onCloseHandler={() => setError(null)} />;
  }

  return (
    <Container>
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h2>Sucursales</h2>
        {authContext.tokenCan("Sucursal:store") && (
          <Button
            variant="success"
            onClick={() => navigate("/main/catalogos/sucursales/crear")}
          >
            <FontAwesomeIcon icon={faSquarePlus as IconProp} /> Nueva Sucursal
          </Button>
        )}
      </div>

      {/* Filtro por plataforma */}
      <div className="d-flex align-items-center mb-3">
        <Form.Label className="me-2 mb-0" style={{ whiteSpace: "nowrap" }}>
          Filtrar por plataforma:
        </Form.Label>
        <Form.Select
          style={{ maxWidth: "250px" }}
          value={plataformaFiltro ?? ""}
          onChange={(e) => {
            const value = e.target.value;
            setPlataformaFiltro(value ? parseInt(value) : null);
          }}
        >
          <option value="">Todas las plataformas</option>
          {authContext.plataformas.map((p) => (
            <option key={p.id} value={p.id}>
              {p.nombre}
            </option>
          ))}
        </Form.Select>
      </div>

      <CustomTable data={sucursales} columns={columns} />
    </Container>
  );
};

export default SucursalesTable;
